import { Avatar, AvatarFallback, initials } from "@hulahoop/ui";
import { useTranslation } from "react-i18next";
import { CommentsPanel } from "@/components/shared/overlays/CommentsPanel";
import { CommentsPanelHeader } from "@/components/shared/overlays/CommentsPanelHeader";
import { CommentsScopeMenu } from "@/components/shared/overlays/CommentsScopeMenu";
import { CommentsThreadList } from "@/components/shared/overlays/CommentsThreadList";
import { CommentsThreadView } from "@/components/shared/overlays/CommentsThreadView";
import { CommentsComposer } from "@/components/shared/forms/CommentsComposer";
import type { useCommentsPanel } from "@/presentation/hooks/useCommentsPanel";
import { formatCompactRelative } from "@/utils/dateFormatters";

export function ShellCommentsPanel({
  comments,
}: {
  comments: ReturnType<typeof useCommentsPanel>;
}) {
  const { t } = useTranslation();
  const { state, labels, actor, list, resolved, summary, detail, people, busy, run } = comments;
  const thread = detail.data;
  const relativeTime = (date: string) => formatCompactRelative(date, t);
  const link = new URL(comments.subjectHref ?? "/activity", document.baseURI);
  if (thread) link.searchParams.set("thread", thread.uuid);
  const threadLink = link.toString();
  const header = (
    <CommentsPanelHeader
      thread={
        thread
          ? {
              resolved: !!thread.resolved_at,
              muted: thread.muted,
              own: actor?.uuid === thread.author?.uuid,
            }
          : null
      }
      count={comments.canQuery ? (summary.data?.open_count ?? 0) : 0}
      busy={busy}
      labels={labels}
      filter={
        <CommentsScopeMenu
          status={state.status}
          onStatus={state.setStatus}
          openCount={summary.data?.open_count ?? 0}
          resolvedCount={resolved.data?.pages[0]?.total_count ?? 0}
          mode={comments.canChooseScope ? state.mode : undefined}
          onMode={state.setMode}
          labels={labels}
        />
      }
      onBack={() => state.setThread(null)}
      onClose={comments.close}
      onResolve={() => {
        if (thread)
          void run({ action: thread.resolved_at ? "reopen" : "resolve", uuid: thread.uuid });
      }}
      onMute={() => {
        if (thread) void run({ action: thread.muted ? "unmute" : "mute", uuid: thread.uuid });
      }}
      onCopy={() => void navigator.clipboard.writeText(threadLink)}
      onDelete={() =>
        thread ? run({ action: "deleteThread", uuid: thread.uuid }) : Promise.resolve(false)
      }
    />
  );
  if (state.thread && thread)
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <CommentsThreadView
          thread={thread}
          actor={actor}
          people={people.data ?? []}
          labels={labels}
          href={comments.subjectHref}
          link={threadLink}
          global={!comments.record}
          busy={busy}
          relativeTime={relativeTime}
          onReply={(body) => run({ action: "reply", uuid: thread.uuid, body })}
          onEdit={(uuid, body) =>
            void run({ action: "editMessage", thread: thread.uuid, uuid, body })
          }
          onDelete={(uuid) => void run({ action: "deleteMessage", thread: thread.uuid, uuid })}
          onReact={(uuid, emoji, remove) =>
            void run({
              action: remove ? "removeReaction" : "react",
              thread: thread.uuid,
              uuid,
              emoji,
            })
          }
        />
      </div>
    );
  const threads = comments.canQuery ? (list.data?.pages.flatMap((page) => page.threads) ?? []) : [];
  return (
    <CommentsPanel
      header={header}
      loading={state.thread ? detail.isLoading : list.isLoading || list.isFetchingNextPage}
      error={state.thread ? detail.isError : list.isError}
      empty={!state.thread && threads.length === 0}
      resolved={state.status === "resolved"}
      labels={labels}
      onRetry={() => void (state.thread ? detail.refetch() : list.refetch())}
      onScroll={(event) => {
        const node = event.currentTarget;
        if (
          node.scrollTop + node.clientHeight >= node.scrollHeight - 240 &&
          list.hasNextPage &&
          !list.isFetching
        )
          void list.fetchNextPage();
      }}
    >
      {!state.thread && (
        <CommentsThreadList
          threads={threads}
          global={!comments.record}
          labels={labels}
          relativeTime={relativeTime}
          onOpen={state.setThread}
        />
      )}
      {!state.thread && comments.canCreate && (
        <div className="px-3 pt-2 pb-3">
          <CommentsComposer
            avatar={
              <Avatar size="xs">
                <AvatarFallback>{initials(actor?.name ?? "")}</AvatarFallback>
              </Avatar>
            }
            people={people.data ?? []}
            labels={labels}
            placeholder={labels.new_placeholder}
            actionLabel={labels.comment}
            disabled={busy}
            onSubmit={(body) =>
              comments.subjectType && comments.record
                ? run({
                    action: "create",
                    draft: {
                      subject_type: comments.subjectType,
                      subject_uuid: comments.record.uuid,
                      body,
                    },
                  })
                : Promise.resolve(false)
            }
          />
        </div>
      )}
    </CommentsPanel>
  );
}
